// Models
import { GroupVisibilityMode } from "../../service/graph/enumerations/GroupVisibilityMode";
import { PatchableODataEntity } from "../planner/common/PatchableODataEntity";
import { SensitivityLabel } from "../tenant/SensitivityLabel";
// Utilities
import * as StringHelper from "@fluentui/utilities";
import { Temporal } from "temporal-polyfill-lite";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import isEmpty from "lodash/isEmpty";
import { ODataVersion } from "../../service/planner/common/IOdataEntityResource";
/**
 * Entity representing a modern group in the client.
 */ export class ModernGroup extends PatchableODataEntity {
    mapEntityChangesToResource(changed, source) {
        return {
            assignedLabels: changed.assignedSensitivityLabelId !== undefined ? this.serializeAssignedLabels() : undefined,
            classification: changed.classification,
            description: changed.description,
            displayName: changed.displayName,
            mailNickname: changed.mailNickname,
            visibility: changed.visibility
        };
    }
    /**
     * Sets a mutable property of the modern group.
     * Throws an error if the property is read-only.
     * @param key - The property key to set.
     * @param value - The value to set for the property.
     * @returns A new instance of IModernGroup with the updated property.
     */ setProperty(key, value) {
        // Only allow setting of mutable properties
        if (key === "visibility" && this.visibility === GroupVisibilityMode.HiddenMembership) {
            throw new Error("ForbiddenException: Cannot change visibility of hidden groups");
        }
        const clone = this.getClone();
        clone[key] = value;
        return clone;
    }
    forSerialization() {
        const graphResource = {
            classification: this.classification || null,
            createdDateTime: this.createdDateTime?.toString(),
            description: this.description || null,
            displayName: this.displayName,
            groupTypes: [
                "Unified"
            ],
            id: this.id,
            // mail is read-only and should not be serialized, see https://docs.microsoft.com/en-us/graph/api/resources/group?view=graph-rest-beta#properties
            mailEnabled: true,
            mailNickname: this.mailNickname,
            securityEnabled: false,
            visibility: this.visibility,
            "@odata.etag": this.version
        };
        if (!isEmpty(this.assignedSensitivityLabelId)) {
            graphResource.assignedLabels = this.serializeAssignedLabels();
        }
        return graphResource;
    }
    getClone() {
        const modernGroupResource = this.forSerialization();
        // mail is read-only and isn't serialized, so we need to set it manually
        modernGroupResource.mail = this.mail;
        // imageSource is not part of the graph resource, so we need to set it manually
        modernGroupResource["imageSource"] = this.imageSource;
        return new ModernGroup({
            assignedSensitivityLabelId: this.assignedSensitivityLabelId,
            classification: this.classification,
            createdDateTime: this.createdDateTime,
            description: this.description,
            displayName: this.displayName,
            id: this.id,
            imageSource: this.imageSource,
            mail: this.mail,
            mailNickname: this.mailNickname,
            visibility: this.visibility,
            version: this.version
        });
    }
    getDetailedInfo(sensitivityLabels) {
        const visibility = this.visibility === GroupVisibilityMode.Public ? Strings.UnifiedGroup_Public : Strings.UnifiedGroup_Private;
        let sensitivityOrClassification = "";
        if (this.assignedSensitivityLabelId && sensitivityLabels) {
            const sensitivity = SensitivityLabel.getFullSensitivityTitle(this.assignedSensitivityLabelId, sensitivityLabels);
            if (sensitivity) {
                sensitivityOrClassification = StringHelper.format(Strings.UnifiedGroup_ClassificationOrSensitivityInfo, sensitivity);
            }
        }
        if (!sensitivityOrClassification && this.classification) {
            sensitivityOrClassification = StringHelper.format(Strings.UnifiedGroup_ClassificationOrSensitivityInfo, this.classification);
        }
        return StringHelper.format(Strings.UnifiedGroup_DetailedInfo, visibility, sensitivityOrClassification).trim();
    }
    /**
     * Build from a modernGroup resource entity as initialization data.
     * @param modernGroupResource ModernGroup resource entity to use for initializing the modernGroup builder
     */ static fromResource(modernGroupResource) {
        let visibility;
        const visibilityString = modernGroupResource.visibility?.toLowerCase() ?? "";
        switch(visibilityString){
            case "hiddenmembership":
                visibility = GroupVisibilityMode.HiddenMembership;
                break;
            case "private":
                visibility = GroupVisibilityMode.Private;
                break;
            case "public":
            default:
                visibility = GroupVisibilityMode.Public;
                break;
        }
        const hasLabels = modernGroupResource.assignedLabels != null && modernGroupResource.assignedLabels.length > 0;
        return new ModernGroup({
            assignedSensitivityLabelId: hasLabels ? modernGroupResource.assignedLabels[0].labelId ?? "" : "",
            classification: modernGroupResource.classification,
            createdDateTime: modernGroupResource.createdDateTime ? Temporal.Instant.from(modernGroupResource.createdDateTime) : undefined,
            description: modernGroupResource.description,
            displayName: modernGroupResource.displayName,
            id: modernGroupResource.id,
            imageSource: modernGroupResource["imageSource"] ?? undefined,
            mail: modernGroupResource.mail,
            mailNickname: modernGroupResource.mailNickname,
            visibility: visibility,
            version: modernGroupResource["@odata.etag"]
        });
    }
    forCreate() {
        return {
            classification: this.classification || null,
            description: this.description || null,
            displayName: this.displayName,
            id: this.id,
            mailEnabled: true,
            mailNickname: this.mailNickname,
            visibility: this.visibility
        };
    }
    serializeAssignedLabels() {
        if (!isEmpty(this.assignedSensitivityLabelId)) {
            return [
                {
                    labelId: this.assignedSensitivityLabelId
                }
            ];
        }
        return [];
    }
    /**
     * Initializes a new instance of the modern group entity
     */ constructor(propertyBag){
        super(propertyBag, ODataVersion.Legacy);
        this.assignedSensitivityLabelId = propertyBag.assignedSensitivityLabelId ?? "";
        this.classification = propertyBag.classification ?? null;
        this.createdDateTime = propertyBag.createdDateTime;
        this.description = propertyBag.description ?? null;
        this.displayName = propertyBag.displayName;
        this.imageSource = propertyBag.imageSource ?? "";
        this.mail = propertyBag.mail;
        this.mailNickname = propertyBag.mailNickname;
        this.visibility = propertyBag.visibility ?? GroupVisibilityMode.Private;
    }
}
/** Max length of group display name per Microsoft Graph API */ ModernGroup.DisplayNameMaxLength = 256;
/** Max length of an email address local part in part@domain */ ModernGroup.MailNicknameMaxLength = 64;
/** Determined experimentally via graph explorer */ ModernGroup.DescriptionMaxLength = 1000;
